import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AssignParcelClerksDto,
  PlaceInListDto,
  RouteInListDto,
  RouteManagementApi,
  TenantUsersApi,
} from '@buspro/web/apis';
import { ErrorDisplayComponent, ProblemDetail, useModalErrorHandler } from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-assign-parcel-clerks-modal',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    NzModalModule,
    NzSelectModule,
    ReactiveFormsModule,
    FormsModule,
    ErrorDisplayComponent,
  ],
  template: `
    <nz-modal
      (nzOnCancel)="onHide()"
      (nzOnOk)="update()"
      [nzClosable]="true"
      [nzOkDisabled]="false"
      [nzVisible]="show"
      [nzWidth]="400"
      nzOkText="Assign"
    >
      <div *nzModalTitle class="font-semibold">Assign Parcel Clerks</div>
      <div *nzModalContent>
        <app-error-display *ngIf="showError() && errorDetails" [error]="errorDetails" (dismiss)="onDismissError()" (retry)="update()"></app-error-display>
        <nz-form-item>
          <nz-form-label [nzRequired]="true" nzSpan="24"
            >Select Parcel Clerks for {{ route.name }} route
          </nz-form-label>
          <nz-form-control nzSpan="24">
            <ng-container *ngIf="!parcelClerksQuery.isLoading(); else loadingTpl">
              <ng-container *ngIf="parcelClerks.length; else emptyStateTpl">
                <nz-select
                  nzMode="multiple"
                  nzShowSearch="true"
                  [nzLoading]="parcelClerksQuery.isLoading()"
                  [(ngModel)]="listOfSelectedParcelClerksIds"
                >
                  <nz-option
                    *ngFor="let su of parcelClerks"
                    [nzLabel]="su.name"
                    [nzValue]="su.id"
                  >
                  </nz-option>
                </nz-select>
                <ng-template #tagPlaceHolder let-selectedList>
                  and {{ selectedList.length }} more selected
                </ng-template>
              </ng-container>
              <ng-template #emptyStateTpl>
                <p class="text-sm text-gray-600">
                  No eligible parcel clerks found for this route. Make sure clerks are assigned to the start or end
                  terminals of {{ route.name }} before assigning them here.
                </p>
              </ng-template>
            </ng-container>
            <ng-template #loadingTpl>
              <p class="text-sm text-gray-600">Loading eligible parcel clerks…</p>
            </ng-template>
          </nz-form-control>
        </nz-form-item>
      </div>
    </nz-modal>
  `,
  styles: ``,
})
export class AssignParcelClerksModalComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private usersApi = inject(TenantUsersApi);
  private routesApi = inject(RouteManagementApi);

  @Input()
  show = false;

  @Input()
  terminal!: PlaceInListDto;

  @Input()
  route!: RouteInListDto;

  @Output()
  hide = new EventEmitter(true);

  form = this.formBuilder.group({
    parcelManagerId: ['', [Validators.required]],
  });

  parcelClerksQuery = injectQuery(() => ({
    queryKey: ['parcel-clerks-eligible-for-route', this.route?.id ?? ''],
    queryFn: () =>
      lastValueFrom(
        this.usersApi.tenantUsersControllerGetParcelClerksEligibleForRoute({
          routeId: this.route.id,
        }),
      ),
    enabled: !!this.route?.id,
  }));
  listOfSelectedParcelClerksIds: string[] = [];

  get parcelClerks() {
    return this.parcelClerksQuery.data() ?? [];
  }

  private modalErrorHandler = useModalErrorHandler();
  showError = signal(false);
  errorDetails: ProblemDetail | null = null;

  constructor(private notification: NzNotificationService) {}

  ngOnInit(): void {
    this.parcelClerksQuery.refetch();
  }

  onHide() {
    this.hide.emit(true);
    this.show = false;
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  update() {
    this.clearErrors();

    const payload: AssignParcelClerksDto = {
      parcelClerksIds: this.listOfSelectedParcelClerksIds,
    };

    this.routesApi.routeControllerAssignParcelClerks({ id: this.route.id, body: payload }).subscribe(
      () => {
        this.clearErrors();
        this.form.reset();
        this.notification.success(
          `${this.listOfSelectedParcelClerksIds.length} parcel clerks assigned to ${this.route.name}`,
          '',
        );
        this.hide.emit();
      },
      (error) => {
        const result = this.modalErrorHandler.handleModalError(error, {
          modalName: 'assign-parcel-clerks',
          action: 'assign-parcel-clerks',
        });
        this.errorDetails = result.problemDetail ?? null;
        this.showError.set(!!this.errorDetails);
        if (!result.shouldHandleGlobally) {
          this.notification.error('Assignment Failed', result.userMessage);
        }
      },
    );
  }
}
